import React, { useEffect, useLayoutEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FLOW_GROUP_NAMES, flowMarkName } from "../constants/flowPresentation";
import { useFlowActions } from "../contexts/FlowActionsContext";
import FlowGradeBubble from "./FlowGradeBubble";
import FlowMarkInput from "./FlowMarkInput";
import FlowGradeGuidance from "./FlowGradeGuidance";
import { MOTION_DURATION, MOTION_EASE, MOTION_SPRING } from "../../../shared/constants/motion";
import { clampScores } from "../utils/markBounds";

export default function FlowSubjectRow({ groupId, choices, levelControl, fields, scores, onScoresChange, grade, neededText, pointsAboveBoundary, fallbackBoundaryNote, error }) {
  const actions = useFlowActions();
  const reduceMotion = useReducedMotion();
  const boundedScores = clampScores(scores, fields);
  useLayoutEffect(() => {
    if (boundedScores !== scores) onScoresChange(current => clampScores(current, fields));
  }, [boundedScores, scores, fields, onScoresChange]);
  useEffect(() => actions?.register(String(groupId), {
    ready: fields.length > 0,
    example: () => onScoresChange(Object.fromEntries(fields.map(([key, , max], index) => [key, String(Math.round(max * [0.76, 0.81, 0.73, 0.85][(Number(groupId) - 1 + index) % 4]))]))),
    clear: () => onScoresChange(Object.fromEntries(fields.map(([key]) => [key, ""]))),
  }), [actions, fields, groupId, onScoresChange]);
  const remaining = fields.filter(([key]) => boundedScores[key] === "" || boundedScores[key] == null).length;
  const weighted = fields.reduce((sum, [key, , max, weight]) => sum + Number(boundedScores[key] || 0) / max * parseFloat(weight), 0);
  return (
    <section className="subject-line" id={`g${groupId}`} aria-labelledby={`subject-${groupId}`}>
      <div className="subject-choice">
        <div className="subject-header"><h3 id={`subject-${groupId}`}><span>{groupId}</span>{FLOW_GROUP_NAMES[Number(groupId) - 1]}</h3>{levelControl}</div>
        <div className="choices">{choices}</div>
      </div>
      <div className="subject-assessment">
        <motion.div layout={!reduceMotion} className="assessment-fields" data-count={fields.length} style={{ "--field-count": Math.max(1, fields.length) }} transition={MOTION_SPRING.gentle}>
          {fields.map(([key, name, max, weight], index) => <motion.div key={`${name}-${max}`} initial={reduceMotion ? false : { opacity: 0, y: 8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: MOTION_DURATION.base, delay: index * MOTION_DURATION.micro / 3, ease: MOTION_EASE.snap }}><FlowMarkInput label={flowMarkName(key, name)} weight={weight} pointsPerMark={parseFloat(weight) / max} max={max} value={boundedScores[key]} onChange={value => onScoresChange(current => ({ ...current, [key]: value }))} ariaLabel={`Subject ${groupId}: ${flowMarkName(key, name)} marks`} /></motion.div>)}
        </motion.div>
        <FlowGradeGuidance grade={grade} neededText={neededText} pointsAboveBoundary={pointsAboveBoundary} />
        <p className="result-note">{error || fallbackBoundaryNote || ""}</p>
      </div>
      <div className="subject-result">
        <span className="grade-label">Grade</span>
        <FlowGradeBubble value={grade} outOf={7} title={neededText || (pointsAboveBoundary != null ? `${pointsAboveBoundary.toFixed(1)} percentage points above the grade ${grade} boundary.` : undefined)} />
        <p className="result-detail">{error ? "No boundary found" : grade == null ? `${remaining} ${remaining === 1 ? "mark" : "marks"} to enter` : `${weighted.toFixed(1)}% weighted mark`}</p>
      </div>
    </section>
  );
}
